import React from 'react';
import { GlobalToken } from 'antd';

import {
  CompressorActivityStateEnum,
  CompressorExtendedActivityEnum,
  ControlModeEnum,
  DeviceConnectivity,
} from '@/constants/enums';
import ConnectivityStandByIcon from '@/comp/common/icons/ConnectivityStandByIcon';
import ConnectivityUnloadIcon from '@/comp/common/icons/ConnectivityUnloadIcon';
import ConnectivityLoadIcon from '@/comp/common/icons/ConnectivityLoadIcon';
import ConnectivityModulatingIcon from '@/comp/common/icons/ConnectivityModulatingIcon';
import ConnectivityOffIcon from '@/comp/common/icons/ConnectivityOffIcon';
import ConnectivityEmergencyStopIcon from '@/comp/common/icons/ConnectivityEmergencyStopIcon';
import ConnectivityDisconnectedIcon from '@/comp/common/icons/ConnectivityDisconnectedIcon';

export interface GetConnectivityParams {
  connectivity: DeviceConnectivity;
  activity: CompressorActivityStateEnum;
  extendedActivity?: CompressorExtendedActivityEnum;
  controlMode: ControlModeEnum;
  isSelectedOnMap: boolean;
  size: string;
  shouldHideTooltip?: boolean;
}

export type StatusAndShape = {
  status: string;
  shape: JSX.Element;
};

export type ControlTypesMapping = {
  shapeText: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
};

export const controllableMap: Record<string, Record<string, ControlTypesMapping>> = {
  [DeviceConnectivity.Connected]: {
    [CompressorExtendedActivityEnum.Standby]: {
      shapeText: 'Standby',
      icon: ConnectivityStandByIcon,
    },
    [CompressorExtendedActivityEnum.Starting]: {
      shapeText: 'Starting',
      icon: ConnectivityUnloadIcon,
    },
    [CompressorExtendedActivityEnum.Stopping]: {
      shapeText: 'Stopping',
      icon: ConnectivityUnloadIcon,
    },
    [CompressorExtendedActivityEnum.EmergencyStop]: {
      shapeText: 'E-Stop',
      icon: ConnectivityEmergencyStopIcon,
    },
    [CompressorExtendedActivityEnum.FullLoad]: {
      shapeText: 'Full Load',
      icon: ConnectivityLoadIcon,
    },
    [CompressorExtendedActivityEnum.Trim]: {
      shapeText: 'Trimming',
      icon: ConnectivityModulatingIcon,
    },
    [CompressorExtendedActivityEnum.Modulation]: {
      shapeText: 'Modulating',
      icon: ConnectivityModulatingIcon,
    },
    [CompressorExtendedActivityEnum.ManualStop]: {
      shapeText: 'Manual Stop',
      icon: ConnectivityOffIcon,
    },
    [CompressorExtendedActivityEnum.RemoteStop]: {
      shapeText: 'Remote Stop',
      icon: ConnectivityOffIcon,
    },
    [CompressorActivityStateEnum.Load]: {
      shapeText: 'Load',
      icon: ConnectivityLoadIcon,
    },
    [CompressorActivityStateEnum.Unload]: {
      shapeText: 'Unload',
      icon: ConnectivityUnloadIcon,
    },
    [CompressorActivityStateEnum.Off]: {
      shapeText: 'Off',
      icon: ConnectivityOffIcon,
    },
    default: {
      shapeText: 'N/A',
      icon: ConnectivityDisconnectedIcon,
    },
  },
  [DeviceConnectivity.Disconnected]: {
    default: {
      shapeText: 'Disconnected',
      icon: ConnectivityDisconnectedIcon,
    },
  },
  [DeviceConnectivity.ConfigurationFailed]: {
    default: {
      shapeText: 'Bad Configuration',
      icon: ConnectivityDisconnectedIcon,
    },
  },
  [DeviceConnectivity.PortDoesNotExist]: {
    default: {
      shapeText: 'COM Port Error',
      icon: ConnectivityDisconnectedIcon,
    },
  },
  [DeviceConnectivity.DeviceChecksumError]: {
    default: {
      shapeText: 'Checksum Error',
      icon: ConnectivityDisconnectedIcon,
    },
  },
};

export const onOffMap: ControlTypesMapping = {
  shapeText: 'N/A',
  icon: ConnectivityDisconnectedIcon,
};

export function getKeyFromMap(
  map: Record<string, ControlTypesMapping | Record<string, ControlTypesMapping>>,
  value: string,
): Record<string, ControlTypesMapping> | ControlTypesMapping {
  if (!map) return;
  return Object.entries(map).find(
    ([key]) => typeof key === 'string' && typeof value === 'string' && key.toLowerCase() === value?.toLowerCase(),
  )?.[1];
}

export function getStatusAndShape(
  mapping: {
    shapeText: string;
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  },
  t,
): StatusAndShape {
  if (!mapping)
    mapping = {
      shapeText: 'N/A',
      icon: ConnectivityDisconnectedIcon,
    };

  return {
    status: t(mapping.shapeText),
    shape: <mapping.icon className={`connectivity-icon`} />,
  };
}

export interface ConnectivityIconWrapperProps {
  size: string;
  theme: { token: GlobalToken };
}
